// ---------- Helper functions ----------

/**
 * Get the raw crop rectangle from metadata sources
 * Priority: recrop > top-level crop > sentinel > face metadata
 *
 * @returns {{ rawRect: any, needsScaling: boolean } | null}
 */
function getCropSource(metaDoc, selectedFace) {
  // User's last recrop choice (already full-frame)
  const recropRect = metaDoc?.pipeline?.recrop?.crop;
  if (recropRect) {
    return { rawRect: recropRect, needsScaling: false };
  }

  // V2: crop stored at top level
  const topLevelCrop = metaDoc?.crop?.box;
  const cropMethod = metaDoc?.crop?.method;
  if (topLevelCrop) {
    // user_recrop = full-frame coords, anything else = detect-space (2000px)
    return { rawRect: topLevelCrop, needsScaling: cropMethod !== 'user_recrop' };
  }

  // Detect-space sources that need scaling
  const sentinelRect =
    metaDoc?.pipeline?.sentinel?.final_crop_rect ||
    metaDoc?.pipeline?.sentinel?.winner_crop_full;
  if (sentinelRect) {
    return { rawRect: sentinelRect, needsScaling: true };
  }

  // Face metadata (detect-space)
  const faceRect =
    selectedFace?.crop_rect ||
    selectedFace?.meta?.crop_rect ||
    selectedFace?.meta?.final_crop_rect ||
    selectedFace?.meta?.winner_crop_full;
  if (faceRect) {
    return { rawRect: faceRect, needsScaling: true };
  }

  return null;
}

/**
 * Normalize crop to [x1, y1, x2, y2] array format (XYXY per CLAUDE.md)
 *
 * @param {any} rawRect - Raw rectangle (array or object)
 * @returns {number[] | null} - [x1, y1, x2, y2] or null
 */
function normalizeRectToArray(rawRect) {
  if (!rawRect) return null;

  if (Array.isArray(rawRect) && rawRect.length === 4) {
    return [...rawRect]; // Copy array format (XYXY)
  }

  if (typeof rawRect === 'object' && 'x1' in rawRect) {
    // Dict format (XYXY) - convert to array (XYXY)
    return [rawRect.x1, rawRect.y1, rawRect.x2, rawRect.y2];
  }

  return null;
}

/**
 * Get image dimensions from metadata
 *
 * Priority: Sati > EXIF > selectedPhoto
 *
 * @returns {{ imgW: number | undefined, imgH: number | undefined }}
 */
function getImageDimensions(metaDoc, selectedPhoto) {
  const sati = metaDoc?.pipeline?.sati;
  const exif = metaDoc?.exiftool;
  return {
    imgW: sati?.original_dims?.width || exif?.ImageWidth || selectedPhoto?.image_width,
    imgH: sati?.original_dims?.height || exif?.ImageHeight || selectedPhoto?.image_height,
  };
}

/**
 * Get detect-space dimensions from Sentinel metadata
 *
 * For portrait images, detect width < 2000 (e.g., 1333x2000).
 * This is critical for correct coordinate scaling.
 *
 * IMPORTANT: Uses smart fallback based on is_portrait when sentinel
 * image_dims are not available. Hardcoded 2000x1333 defaults cause
 * ~33% position error on portrait images.
 *
 * @param {Object} metaDoc - Photo metadata document
 * @returns {{ detectW: number, detectH: number }}
 */
function getDetectDimensions(metaDoc) {
  const sentinel = metaDoc?.pipeline?.sentinel;
  const imageDims = sentinel?.image_dims;

  // Use actual dimensions if available
  if (imageDims?.width && imageDims?.height) {
    return {
      detectW: imageDims.width,
      detectH: imageDims.height,
    };
  }

  // Smart fallback based on is_portrait flag
  // Check both top-level and sati locations for original_dims
  const originalDims = metaDoc?.original_dims || metaDoc?.pipeline?.sati?.original_dims;
  const isPortrait = originalDims?.is_portrait;

  if (isPortrait) {
    return { detectW: 1333, detectH: 2000 };
  }
  return { detectW: 2000, detectH: 1333 };
}

/**
 * Get actual cropped output dimensions from Seraph diagnostics
 *
 * @returns {{ actualCropW: number | undefined, actualCropH: number | undefined }}
 */
function getActualCropDimensions(metaDoc) {
  const seraphDiags = metaDoc?.pipeline?.seraph?.diagnostics || [];
  const loadStep = seraphDiags.find((d) => d.step === 'load');
  return {
    actualCropW: loadStep?.extra?.width,
    actualCropH: loadStep?.extra?.height,
  };
}

/**
 * Scale rectangle from detect-space to source-space
 *
 * For portrait images, detect width < 2000 (e.g., 1333x2000).
 * Using hardcoded 2000 causes ~33% position error on portrait images.
 *
 * @param {number[]} rect - [x1, y1, x2, y2]
 * @param {number} imgW - Source image width
 * @param {number} imgH - Source image height
 * @param {number} detectW - Detect-space width (from Sentinel image_dims)
 * @param {number} detectH - Detect-space height (from Sentinel image_dims)
 * @returns {number[]} - Scaled [x1, y1, x2, y2]
 */
function scaleRectToSourceSpace(rect, imgW, imgH, detectW, detectH) {
  // Use separate scale factors for X and Y to handle non-uniform scaling
  const scaleX = imgW / detectW;
  const scaleY = imgH / detectH;
  return [
    Math.round(rect[0] * scaleX),
    Math.round(rect[1] * scaleY),
    Math.round(rect[2] * scaleX),
    Math.round(rect[3] * scaleY),
  ];
}

/**
 * Validate and adjust dimensions if crop extends beyond frame
 *
 * @param {number[]} rect - [x1, y1, x2, y2]
 * @param {number} imgW - Image width
 * @param {number} imgH - Image height
 * @returns {{ imgW: number, imgH: number }}
 */
function validateDimensions(rect, imgW, imgH) {
  const [, , x2, y2] = rect;
  // If crop extends beyond claimed full-frame, use crop bounds as minimum
  return {
    imgW: Math.max(imgW, x2),
    imgH: Math.max(imgH, y2),
  };
}

// ---------- Main function ----------

/**
 * Compute crop box from photo metadata.
 * Handles different coordinate spaces and scaling.
 *
 * @param {Object} metaDoc - Photo metadata document
 * @param {Object} selectedPhoto - Selected photo object
 * @param {Object} selectedFace - Selected face object (optional)
 * @returns {Object|null} Crop box with x1, y1, x2, y2 (XYXY), imgW, imgH, actualCropW, actualCropH
 */
export function computeCropBox(metaDoc, selectedPhoto, selectedFace = null) {
  // Crop history: a confirmed draft or published recrop (lead_crop, newest
  // history row) outranks the meta-derived auto-crop. Already source-space —
  // no scaling. A selected face still wins (face-scoped crop view).
  const lead = selectedPhoto?.lead_crop;
  if (!selectedFace && Array.isArray(lead?.box) && lead.box.length === 4) {
    const [x1, y1, x2, y2] = lead.box;
    if (x2 > x1 && y2 > y1) {
      let { imgW, imgH } = getImageDimensions(metaDoc, selectedPhoto);
      if (imgW && !imgH) imgH = Math.round(imgW / 1.5);
      const { actualCropW, actualCropH } = getActualCropDimensions(metaDoc);
      const dims = validateDimensions([x1, y1, x2, y2], imgW || x2, imgH || y2);
      return {
        x1, y1, x2, y2,
        imgW: dims.imgW,
        imgH: dims.imgH,
        actualCropW,
        actualCropH,
        rotation: lead.rotation || 0,
      };
    }
  }

  if (!metaDoc) return null;

  // Get crop source and determine if scaling is needed
  const cropSource = getCropSource(metaDoc, selectedFace);
  if (!cropSource) return null;

  // Normalize to array format
  let rect = normalizeRectToArray(cropSource.rawRect);
  if (!rect) return null;

  // Get image dimensions (source/original space)
  let { imgW, imgH } = getImageDimensions(metaDoc, selectedPhoto);

  // Get detect-space dimensions from Sentinel
  // CRITICAL: For portrait images, detectW < 2000 (e.g., 1333x2000)
  const { detectW, detectH } = getDetectDimensions(metaDoc);

  // Get actual crop dimensions from Seraph
  const { actualCropW, actualCropH } = getActualCropDimensions(metaDoc);

  // Scale from detect-space to source-space if needed
  if (imgW && imgH && cropSource.needsScaling) {
    rect = scaleRectToSourceSpace(rect, imgW, imgH, detectW, detectH);
  }

  // Fallback: infer height from 3:2 aspect if only width available
  if (imgW && !imgH && rect) {
    imgH = Math.round(imgW / 1.5);
  }

  // Validate we have required data
  if (!rect || !imgW || !imgH) return null;

  const [x1, y1, x2, y2] = rect;
  if (x2 <= x1 || y2 <= y1) return null;

  // Validate dimensions against crop bounds
  const validatedDims = validateDimensions(rect, imgW, imgH);
  imgW = validatedDims.imgW;
  imgH = validatedDims.imgH;

  const result = { x1, y1, x2, y2, imgW, imgH, actualCropW, actualCropH };

  return result;
}

/**
 * Clamp crop box to image bounds.
 * With expand=True backend rotation, entire image is valid (no safe zone restriction).
 *
 * @param {Object} box - Crop box {x, y, w, h}
 * @param {number} _rotation - Rotation angle (unused with expand=True)
 * @param {number} containerW - Container width
 * @param {number} containerH - Container height
 * @returns {Object} Clamped crop box {x, y, w, h}
 */
export function clampCropToImage(box, _rotation, containerW, containerH) {
  const minSize = 50;

  // Full image bounds (no safe zone restriction with expand=True)
  const w = Math.max(minSize, Math.min(box.w, containerW));
  const h = Math.max(minSize, Math.min(box.h, containerH));
  const x = Math.max(0, Math.min(box.x, containerW - w));
  const y = Math.max(0, Math.min(box.y, containerH - h));

  return { x, y, w, h };
}
